// In-place reservation modal triggered from a map marker popup. Replaces
// the "navigate to request.html" flow with a quick form that submits to
// POST /api/requests directly. Designer's #book-modal HTML lives in
// map.html; this module wires the open/close + submit behaviour.
//
// Multi-station: the user can add N stations to one reservation by clicking
// "+ Voeg ander station toe" - the modal hides, a picking banner appears,
// and the next marker click on the map (via map.ts) calls back into
// addPickedPoint() to grow the list. All picks share the same project,
// time window, contact, and approval flow; the backend creates one
// tentative Booking per picked station.
import { api } from './api';
import { t } from './i18n/i18n';
import { friendlyError } from './friendlyError';
import { fetchConnectorCatalogue, populateConnectorSelect } from './connectorSelect';
import { setMinToday } from './lib/dateRange';

export interface BookablePoint {
  kind: string;
  id: number;
  name: string;
  address: string;
  type?: string | null;
  maxCurrentA?: number | null;
  connection?: string | null;
  /** The station's own connector types (from the map), used to scope the request's connector dropdown. */
  connectorTypes?: string[];
}

// The map splits charging stations into AC/DC marker layers, but the backend
// has a single ChargingStation PointKind (AC/DC is a Connector.PowerType
// detail). Collapse the map's kind before submitting, else /api/requests
// rejects it with "Unknown PointKind 'AcChargingStation'".
function toBackendPointKind(kind: string): string {
  return kind === 'AcChargingStation' || kind === 'DcChargingStation' ? 'ChargingStation' : kind;
}

// The three extra questions (construction card, adapter, fenced-off) are only
// asked when the requested point is a charging station - both map layers.
function isChargingStationKind(kind: string): boolean {
  return kind === 'AcChargingStation' || kind === 'DcChargingStation';
}

const modal = document.getElementById('book-modal');

/**
 * Picked points for the current reservation. The first entry is the
 * "primary" - its kind/id go on the Request row directly; the rest go
 * to the backend as `additionalPoints` and each gets its own tentative
 * booking sharing the request's time window. Empty when the modal is
 * closed.
 */
let pickedPoints: BookablePoint[] = [];

/**
 * True while the modal is temporarily hidden waiting for the user to click
 * another marker on the map. map.ts checks this to swap the detail card's
 * primary CTA from "Reserveer" to "+ Toevoegen aan reservering".
 */
let pickingMore = false;

if (modal) {
  const formView    = document.getElementById('book-modal-form-view');
  const successView = document.getElementById('book-modal-success-view');
  const form        = document.getElementById('book-modal-form') as HTMLFormElement | null;
  const submitBtn   = document.getElementById('book-modal-submit') as HTMLButtonElement | null;
  const picksHost   = document.getElementById('bm-picked-stations') as HTMLDivElement | null;
  const addBtn      = document.getElementById('bm-add-station-btn') as HTMLButtonElement | null;
  const pickBanner  = document.getElementById('bm-picking-banner') as HTMLDivElement | null;
  const pickCancel  = document.getElementById('bm-picking-cancel') as HTMLButtonElement | null;
  const chargingFields = document.getElementById('bm-charging-fields') as HTMLDivElement | null;

  setMinToday('bm-start', 'bm-end');

  // Show the charging-station questions only for charging-station requests,
  // and keep the radios' `required` flag in lockstep with visibility - a
  // hidden required control would block submit for every other point kind.
  function setChargingFieldsVisible(visible: boolean): void {
    if (!chargingFields) return;
    chargingFields.style.display = visible ? 'block' : 'none';
    chargingFields.querySelectorAll<HTMLInputElement>('input[type="radio"]').forEach((r) => {
      r.required = visible;
      if (!visible) r.checked = false;
    });
  }

  function showModal(): void {
    modal!.classList.add('is-open');
    modal!.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  function hideModal(): void {
    modal!.classList.remove('is-open');
    modal!.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  function close(): void {
    hideModal();
    pickedPoints = [];
    exitPickingMode();
    if (form) form.reset();
  }

  function enterPickingMode(): void {
    pickingMore = true;
    hideModal();
    // Inline-style toggle (not the `hidden` attribute) - the banner's
    // baseline display is `display:flex`, which would win against the
    // user-agent `[hidden]{display:none}` rule.
    if (pickBanner) pickBanner.style.display = 'flex';
  }

  function exitPickingMode(): void {
    pickingMore = false;
    if (pickBanner) pickBanner.style.display = 'none';
  }

  function escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function renderPicks(): void {
    if (!picksHost) return;
    picksHost.innerHTML = pickedPoints.map((p, idx) => {
      // First pick can't be removed inline - closing the modal entirely is
      // the only way to cancel the whole booking. Allowing × on the primary
      // would leave us with an "empty" reservation and a confusing state.
      const removable = idx > 0;
      const removeBtn = removable
        ? `<button type="button" class="bm-picked-stations__remove" data-remove-idx="${idx}"
                aria-label="${escapeHtml(t('common.btn.close') || 'Verwijder')}"
                style="background:transparent;border:0;cursor:pointer;color:var(--sk-muted);font-size:15px;line-height:1;padding:0 2px;">×</button>`
        : '';
      return `
        <span class="bm-picked-stations__chip"
              style="display:inline-flex;align-items:center;gap:6px;background:var(--sk-blue-100,#eff3fb);
                     border:1px solid var(--sk-border,#d6dce6);border-radius:14px;padding:4px 10px 4px 12px;
                     font-size:12.5px;color:var(--sk-navy-800,#0a2a5c);">
          <span style="font-weight:600;">${escapeHtml(p.name)}</span>
          ${removeBtn}
        </span>`;
    }).join('');
    picksHost.querySelectorAll<HTMLButtonElement>('button[data-remove-idx]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const i = Number(btn.dataset.removeIdx);
        if (Number.isFinite(i) && i > 0 && i < pickedPoints.length) {
          pickedPoints.splice(i, 1);
          renderPicks();
        }
      });
    });
    void refreshConnectorSelect();
  }

  // Fill the connector dropdown from the picked stations' own connectors
  // (union across picks) - or the full catalogue when none are known - plus
  // a trailing "Unknown".
  async function refreshConnectorSelect(): Promise<void> {
    const select = document.getElementById('bm-connector') as HTMLSelectElement | null;
    if (!select) return;
    const stationConnectors = Array.from(new Set(pickedPoints.flatMap((p) => p.connectorTypes ?? [])));
    const catalogue = await fetchConnectorCatalogue();
    populateConnectorSelect(select, stationConnectors, catalogue, {
      placeholder: t('map.book.connector_ph'),
      unknown: t('map.book.connector_unknown'),
    });
  }

  document.getElementById('book-modal-close')?.addEventListener('click', close);
  document.getElementById('book-modal-cancel')?.addEventListener('click', close);
  document.getElementById('book-modal-done')?.addEventListener('click', close);
  modal.addEventListener('click', (e) => { if (e.target === modal) close(); });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (pickingMore) { exitPickingMode(); showModal(); return; }
    if (modal!.classList.contains('is-open')) close();
  });

  addBtn?.addEventListener('click', () => {
    enterPickingMode();
  });

  pickCancel?.addEventListener('click', () => {
    exitPickingMode();
    showModal();
  });

  // "Same as organisation" - copy contact fields into connector fields.
  document.getElementById('bm-copy-aansluiter')?.addEventListener('click', () => {
    if (!form) return;
    const get = (n: string) => (form.elements.namedItem(n) as HTMLInputElement | null)?.value ?? '';
    const set = (n: string, v: string) => { const el = form.elements.namedItem(n) as HTMLInputElement | null; if (el) el.value = v; };
    set('aansluiter_name', get('contact_name'));
    set('aansluiter_email', get('contact_email'));
    set('aansluiter_phone', get('contact_phone'));
    set('aansluiter_company', get('organisation'));
  });

  // "Same as organisation" - fill the billing name + email from the org
  // fields. The rest of the billing block (address, VAT, reference) has no
  // organisation-side equivalent so it's left untouched.
  document.getElementById('bm-copy-finance')?.addEventListener('click', () => {
    if (!form) return;
    const get = (n: string) => (form.elements.namedItem(n) as HTMLInputElement | null)?.value ?? '';
    const set = (n: string, v: string) => { const el = form.elements.namedItem(n) as HTMLInputElement | null; if (el) el.value = v; };
    set('billing_name', get('organisation'));
    set('billing_email', get('contact_email'));
  });

  const errorBox = document.getElementById('book-modal-error');
  const showSubmitError = (message: string): void => {
    if (!errorBox) return;
    errorBox.textContent = message;
    errorBox.hidden = false;
  };
  const hideSubmitError = (): void => { if (errorBox) errorBox.hidden = true; };

  form?.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (pickedPoints.length === 0 || !form) return;
    hideSubmitError();
    const data = new FormData(form);
    if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = t('map.book.submitting'); }

    const primary = pickedPoints[0];
    const extras = pickedPoints.slice(1);
    // Only send the charging-station answers when the primary point is a
    // charging station; the section (and its required radios) is hidden
    // otherwise, so the values would be absent anyway.
    const chargingAnswers = isChargingStationKind(primary.kind)
      ? {
          wantsConstructionChargingCard: data.get('wants_construction_card') === 'yes',
          needsChargingAdapter: data.get('needs_adapter') === 'yes',
          stationBehindFence: data.get('station_behind_fence') === 'yes',
        }
      : {};

    try {
      const client = await api();
      await client.post('/api/requests', {
        pointKind: toBackendPointKind(primary.kind),
        pointId: primary.id,
        additionalPoints: extras.map((p) => ({ pointKind: toBackendPointKind(p.kind), pointId: p.id })),
        projectName: String(data.get('project_name') ?? '') || `Reservation ${primary.name}`,
        organisation: String(data.get('organisation') ?? ''),
        requestedConnectorType: String(data.get('requested_connector_type') ?? '') || null,
        startAt: new Date(String(data.get('start_at') ?? '')).toISOString(),
        endAt: new Date(String(data.get('end_at') ?? '')).toISOString(),
        contactName: String(data.get('contact_name') ?? ''),
        contactEmail: String(data.get('contact_email') ?? ''),
        contactPhone: String(data.get('contact_phone') ?? '') || null,
        connectorName: String(data.get('aansluiter_name') ?? '') || String(data.get('contact_name') ?? ''),
        connectorEmail: String(data.get('aansluiter_email') ?? '') || String(data.get('contact_email') ?? ''),
        connectorPhone: String(data.get('aansluiter_phone') ?? '') || null,
        // Billing details - all optional; send null for anything left blank.
        billingName: String(data.get('billing_name') ?? '') || null,
        billingEmail: String(data.get('billing_email') ?? '') || null,
        billingAddress: String(data.get('billing_address') ?? '') || null,
        billingPostalCode: String(data.get('billing_postcode') ?? '') || null,
        billingCity: String(data.get('billing_city') ?? '') || null,
        billingVatNumber: String(data.get('billing_vat') ?? '') || null,
        billingReference: String(data.get('billing_reference') ?? '') || null,
        ...chargingAnswers,
      });

      if (formView) formView.style.display = 'none';
      if (successView) successView.style.display = 'block';
    } catch (err) {
      showSubmitError(friendlyError(err, t('map.book.submit_failed')));
    } finally {
      if (submitBtn && formView && formView.style.display !== 'none') {
        submitBtn.disabled = false;
        submitBtn.textContent = t('map.book.submit');
      }
    }
  });

  // Expose the open() helper as a named export.
  (window as unknown as { __skBookModal?: { open(point: BookablePoint): void } }).__skBookModal = {
    open(point: BookablePoint) {
      pickedPoints = [point];
      exitPickingMode();
      renderPicks();

      if (form) {
        form.reset();
        const conn = document.getElementById('bm-connection') as HTMLInputElement | null;
        if (conn) conn.placeholder = point.connection ?? '';
      }
      // Reset transient submit state from a previous open: form.reset() clears
      // the fields but not the JS-driven button label/disabled (left in the
      // "Submitting…" state after a successful send) or the error box (which
      // would otherwise show a stale message - e.g. a slot-taken error from a
      // different station).
      if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = t('map.book.submit'); }
      hideSubmitError();
      // Primary point (pickedPoints[0]) is fixed for the modal session, so its
      // kind alone decides whether the charging-station questions apply.
      setChargingFieldsVisible(isChargingStationKind(point.kind));
      void refreshConnectorSelect();

      if (formView) (formView as HTMLElement).style.display = 'block';
      if (successView) (successView as HTMLElement).style.display = 'none';
      showModal();
      setTimeout(() => document.getElementById('bm-start')?.focus(), 80);
    },
  };

  (window as unknown as { __skBookModalAdd?: (p: BookablePoint) => 'added' | 'duplicate' }).__skBookModalAdd = (p: BookablePoint) => {
    const primary = pickedPoints[0];
    if (!primary) return 'duplicate';
    if (pickedPoints.some((e) => e.kind === p.kind && e.id === p.id)) return 'duplicate';
    // Kind/organisation mixing is allowed - the backend derives the request's
    // ConnectionClass from whichever picked station is most demanding (any
    // AC charger ⇒ ac_charger path), and the approval flow stays per-request.
    pickedPoints.push(p);
    renderPicks();
    exitPickingMode();
    showModal();
    return 'added';
  };
}

export function openBookModal(point: BookablePoint): void {
  const handle = (window as unknown as { __skBookModal?: { open(p: BookablePoint): void } }).__skBookModal;
  handle?.open(point);
}

/**
 * True while the modal is hidden waiting for the user to click another marker.
 * Used by map.ts to swap the detail-card primary CTA.
 */
export function isPickingMore(): boolean {
  return pickingMore;
}

/**
 * Add a station to the open booking. Returns:
 *   'added'      → station accepted, modal reopens with new chip.
 *   'duplicate'  → station was already on the list; no-op.
 */
export function addPickedPoint(point: BookablePoint): 'added' | 'duplicate' {
  const fn = (window as unknown as { __skBookModalAdd?: (p: BookablePoint) => 'added' | 'duplicate' }).__skBookModalAdd;
  return fn?.(point) ?? 'duplicate';
}
